import { cn } from '@brew/ui/cn'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import * as React from 'react'

function Breadcrumb(
  props: React.ComponentProps<'nav'> & {
    separator?: React.ReactNode
  }
) {
  const t = useTranslations('ui')
  return <nav aria-label={t('breadcrumb.label')} {...props} />
}

// biome-ignore lint/suspicious/noReactForwardRef: pre-Ultracite baseline — migrate to ref prop when touching
const BreadcrumbList = React.forwardRef<
  HTMLOListElement,
  React.ComponentPropsWithoutRef<'ol'>
>(({ className, ...props }, ref) => (
  <ol
    ref={ref}
    className={cn(
      'flex flex-wrap items-center gap-1.5 break-words text-muted-foreground text-sm sm:gap-2.5',
      className
    )}
    {...props}
  />
))
BreadcrumbList.displayName = 'BreadcrumbList'

// biome-ignore lint/suspicious/noReactForwardRef: pre-Ultracite baseline — migrate to ref prop when touching
const BreadcrumbItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentPropsWithoutRef<'li'>
>(({ className, ...props }, ref) => (
  <li
    ref={ref}
    className={cn('inline-flex items-center gap-1.5', className)}
    {...props}
  />
))
BreadcrumbItem.displayName = 'BreadcrumbItem'

// biome-ignore lint/suspicious/noReactForwardRef: pre-Ultracite baseline — migrate to ref prop when touching
const BreadcrumbLink = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentPropsWithoutRef<typeof Link>
>(({ className, ...props }, ref) => (
  <Link
    ref={ref}
    className={cn(
      'inline-flex items-center gap-1.5 transition-colors hover:text-foreground',
      className
    )}
    {...props}
  />
))
BreadcrumbLink.displayName = 'BreadcrumbLink'

// biome-ignore lint/suspicious/noReactForwardRef: pre-Ultracite baseline — migrate to ref prop when touching
const BreadcrumbPage = React.forwardRef<
  HTMLSpanElement,
  React.ComponentPropsWithoutRef<'span'>
>(({ className, ...props }, ref) => (
  // biome-ignore lint/a11y/useFocusableInteractive: current-page crumb is intentionally non-interactive — role=link + aria-disabled without a tab stop is the upstream shadcn pattern
  <span
    ref={ref}
    role="link"
    aria-disabled="true"
    aria-current="page"
    className={cn('font-normal text-foreground', className)}
    {...props}
  />
))
BreadcrumbPage.displayName = 'BreadcrumbPage'

const BreadcrumbSeparator = ({
  children,
  className,
  ...props
}: React.ComponentProps<'li'>) => (
  <li
    role="presentation"
    aria-hidden="true"
    className={cn('text-muted-foreground/20', className)}
    {...props}
  >
    {children ?? '/'}
  </li>
)
BreadcrumbSeparator.displayName = 'BreadcrumbSeparator'

export {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
}
